import React, { useState, useEffect } from 'react';
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBInput
} from 'mdb-react-ui-kit';
import { useMessageBox } from '../components/Common/MessageBox';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './InviteUsersPage.css';

const InviteUsersPage = () => {
  const { showInfo, showSuccess, showWarning, showError, MessageBoxComponent } = useMessageBox();
  const { user } = useAuth();
  
  // Loading state
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: ''
  });

  // Users data from API
  const [users, setUsers] = useState([]);

  // Get current user's role
  const currentUserRole = user?.role?.toLowerCase() || '';
  const currentUserId = user?.id;

  // Fetch all company users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('📡 FETCHING USERS from /api/users');
      const response = await api.get('/api/users');
      console.log('📥 FETCH USERS RESPONSE:', response.data);
      if (response.data.success) {
        console.log('✅ Users loaded:', response.data.users.length, 'users');
        console.table(response.data.users.map(u => ({ id: u.id, email: u.email, role: u.role })));
        setUsers(response.data.users);
      } else {
        console.warn('⚠️ FETCH returned success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ FETCH USERS ERROR:', error);
      showError(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle form submit
  const handleSubmit = async () => {
    const { firstName, lastName, email, password, role } = formData;

    // Validation
    if (!firstName || !lastName || !email || !password || !role) {
      showWarning('Please fill all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showWarning('Please enter a valid email address');
      return;
    }

    // Password validation
    if (password.length < 6) {
      showWarning('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await api.post('/api/users', formData);
      
      if (response.data.success) {
        // Check if multiple owners warning
        if (response.data.multipleOwners) {
          showInfo('⚠️ Company now has multiple owners');
        }
        
        showSuccess(response.data.message || 'User added successfully!');
        
        // Reset form and close modal
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          role: ''
        });
        setShowModal(false);
        
        // Refresh user list
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adding user:', error);
      const errorMessage = error.response?.data?.message || 'Failed to add user';
      showError(errorMessage);
    }
  };

  // Handle delete user
  const handleDelete = async (userId, userRole) => {
    // Check permissions
    if (currentUserRole === 'editor' && (userRole.toLowerCase() === 'admin' || userRole.toLowerCase() === 'owner')) {
      showError('Editors cannot remove admins or owners');
      return;
    }

    if (currentUserRole === 'admin' && userRole.toLowerCase() === 'owner') {
      showError('Admins cannot remove owners');
      return;
    }

    const isSelf = userId === currentUserId;
    const confirmMessage = isSelf 
      ? '⚠️ Are you sure you want to remove yourself from this company? You will lose access immediately.'
      : `Are you sure you want to remove this user?`;

    if (window.confirm(confirmMessage)) {
      try {
        console.log('🗑️ DELETE REQUEST:', { userId, userRole, endpoint: `/api/users/${userId}` });
        
        const response = await api.delete(`/api/users/${userId}`);
        
        console.log('✅ DELETE RESPONSE:', response.data);
        
        if (response.data.success) {
          showSuccess(response.data.message || 'User removed successfully');
          fetchUsers();
          
          // If user deleted themselves, redirect to login
          if (isSelf) {
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500);
          }
        } else {
          console.warn('⚠️ DELETE returned success=false:', response.data);
          showError('Delete failed: ' + (response.data.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('❌ DELETE ERROR:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          endpoint: `/api/users/${userId}`
        });
        const errorMessage = error.response?.data?.message || `Failed to remove user (${error.response?.status || 'Network Error'})`;
        showError(errorMessage);
      }
    }
  };

  // Handle edit user - open modal
  const handleEdit = (targetUser) => {
    // Check permissions
    if (currentUserRole === 'editor') {
      showError('Editors cannot modify user roles');
      return;
    }

    if (currentUserRole === 'admin' && targetUser.role.toLowerCase() === 'owner') {
      showError('Admins cannot modify owner roles');
      return;
    }

    setEditingUser(targetUser);
    setEditModal(true);
  };

  // Handle role change
  const handleRoleChange = async (newRole) => {
    if (!editingUser) return;

    try {
      console.log('🔄 UPDATE ROLE REQUEST:', { 
        userId: editingUser.id, 
        oldRole: editingUser.role, 
        newRole,
        endpoint: `/api/users/${editingUser.id}` 
      });
      
      const response = await api.put(`/api/users/${editingUser.id}`, { role: newRole });
      
      console.log('✅ UPDATE ROLE RESPONSE:', response.data);
      
      if (response.data.success) {
        // Check for multiple owners warning
        if (response.data.multipleOwners) {
          showInfo('⚠️ ' + response.data.message);
        } else {
          showSuccess(response.data.message || 'Role updated successfully');
        }
        
        setEditModal(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        console.warn('⚠️ UPDATE ROLE returned success=false:', response.data);
        showError('Update failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ UPDATE ROLE ERROR:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        userId: editingUser?.id,
        newRole
      });
      const errorMessage = error.response?.data?.message || `Failed to update role (${error.response?.status || 'Network Error'})`;
      showError(errorMessage);
    }
  };

  return (
    <div className="users-page">
      <div className="page-header-section">
        <div>
          <h1 className="page-title" style={{ color: '#000000' }}>Users Management</h1>
          <p className="page-description">
            Manage team members and their access levels
          </p>
        </div>
        {(currentUserRole === 'owner' || currentUserRole === 'admin') && (
          <button 
            className="add-user-btn"
            onClick={() => setShowModal(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <line x1="20" y1="8" x2="20" y2="14" />
              <line x1="23" y1="11" x2="17" y2="11" />
            </svg>
            <span className="add-user-btn-text">Add User</span>
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading users...</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((targetUser) => {
                const canEdit = (currentUserRole === 'owner' || currentUserRole === 'admin') && 
                                !(currentUserRole === 'admin' && targetUser.role.toLowerCase() === 'owner');
                const canDelete = targetUser.id === currentUserId || 
                                  ((currentUserRole === 'owner' || currentUserRole === 'admin') && 
                                   !(currentUserRole === 'admin' && targetUser.role.toLowerCase() === 'owner'));
                
                return (
                  <tr key={targetUser.id}>
                    <td>{targetUser.firstName} {targetUser.lastName}</td>
                    <td>{targetUser.email}</td>
                    <td>
                      <span className={`role-badge role-${targetUser.role.toLowerCase()}`}>
                        {targetUser.role}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {canEdit && (
                          <button 
                            className="action-icon-btn edit-btn"
                            onClick={() => handleEdit(targetUser)}
                            title="Change role"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        )}
                        {canDelete && (
                          <button 
                            className="action-icon-btn delete-btn"
                            onClick={() => handleDelete(targetUser.id, targetUser.role)}
                            title="Remove user"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <p>No users found</p>
              {(currentUserRole === 'owner' || currentUserRole === 'admin') && (
                <button className="add-user-btn-small" onClick={() => setShowModal(true)}>
                  Add Your First User
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add User Modal */}
      <MDBModal open={showModal} staticBackdrop tabIndex='-1'>
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Add New User
              </MDBModalTitle>
            </MDBModalHeader>
            
            <MDBModalBody>
              <MDBInput
                label="First Name"
                name="firstName"
                size="lg"
                className="mb-3"
                value={formData.firstName}
                onChange={handleChange}
              />
              
              <MDBInput
                label="Last Name"
                name="lastName"
                size="lg"
                className="mb-3"
                value={formData.lastName}
                onChange={handleChange}
              />
              
              <MDBInput
                label="Email Address"
                name="email"
                type="email"
                size="lg"
                className="mb-3"
                value={formData.email}
                onChange={handleChange}
              />
              
              <MDBInput
                label="Password"
                name="password"
                type="password"
                size="lg"
                className="mb-3"
                value={formData.password}
                onChange={handleChange}
              />
              
              <select
                name="role"
                className="form-control form-control-lg mb-3"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="">Select Role</option>
                {/* Only Owners can add new Owners */}
                {currentUserRole === 'owner' && <option value="Owner">Owner</option>}
                <option value="Admin">Admin</option>
                <option value="Editor">Editor</option>
              </select>
            </MDBModalBody>
            
            <MDBModalFooter>
              <MDBBtn 
                color="secondary" 
                onClick={() => {
                  setShowModal(false);
                  setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                    role: ''
                  });
                }}
              >
                Cancel
              </MDBBtn>
              <MDBBtn 
                color="danger"
                onClick={handleSubmit}
              >
                Add User
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Edit Role Modal */}
      <MDBModal open={editModal} staticBackdrop tabIndex='-1'>
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                Change User Role
              </MDBModalTitle>
            </MDBModalHeader>
            
            <MDBModalBody>
              {editingUser && (
                <>
                  <p style={{ marginBottom: '16px' }}>
                    Change role for <strong>{editingUser.firstName} {editingUser.lastName}</strong>
                  </p>
                  <p style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                    Current role: <span className={`role-badge role-${editingUser.role.toLowerCase()}`}>{editingUser.role}</span>
                  </p>
                  
                  <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexDirection: 'column' }}>
                    {/* Only show Owner option if current user is Owner */}
                    {currentUserRole === 'owner' && (
                      <button
                        onClick={() => handleRoleChange('Owner')}
                        disabled={editingUser.role === 'Owner'}
                        style={{
                          padding: '12px',
                          border: '2px solid #dc3545',
                          background: editingUser.role === 'Owner' ? '#f0f0f0' : 'white',
                          borderRadius: '8px',
                          cursor: editingUser.role === 'Owner' ? 'not-allowed' : 'pointer',
                          fontWeight: '500',
                          opacity: editingUser.role === 'Owner' ? 0.5 : 1
                        }}
                      >
                        👑 Owner (Full Access + Can Transfer Ownership)
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleRoleChange('Admin')}
                      disabled={editingUser.role === 'Admin'}
                      style={{
                        padding: '12px',
                        border: '2px solid #0d6efd',
                        background: editingUser.role === 'Admin' ? '#f0f0f0' : 'white',
                        borderRadius: '8px',
                        cursor: editingUser.role === 'Admin' ? 'not-allowed' : 'pointer',
                        fontWeight: '500',
                        opacity: editingUser.role === 'Admin' ? 0.5 : 1
                      }}
                    >
                      🔧 Admin (Can Add/Remove Users, All Pages)
                    </button>
                    
                    <button
                      onClick={() => handleRoleChange('Editor')}
                      disabled={editingUser.role === 'Editor'}
                      style={{
                        padding: '12px',
                        border: '2px solid #6c757d',
                        background: editingUser.role === 'Editor' ? '#f0f0f0' : 'white',
                        borderRadius: '8px',
                        cursor: editingUser.role === 'Editor' ? 'not-allowed' : 'pointer',
                        fontWeight: '500',
                        opacity: editingUser.role === 'Editor' ? 0.5 : 1
                      }}
                    >
                      ✏️ Editor (Products & Dashboard Only)
                    </button>
                  </div>
                </>
              )}
            </MDBModalBody>
            
            <MDBModalFooter>
              <MDBBtn 
                color="secondary" 
                onClick={() => {
                  setEditModal(false);
                  setEditingUser(null);
                }}
              >
                Cancel
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MessageBoxComponent />
    </div>
  );
};

export default InviteUsersPage;
